import React from "react";
import GlobalStyles from "@mui/material/GlobalStyles";

// The motion from the design canvas, as CSS.
//
// Kept as a stylesheet rather than MUI transitions for three reasons: the
// entrances stagger dozens of elements with animation-delay, which is painful
// to thread through sx on every child; a keyframed entrance cannot be written
// as a transition at all; and fills must be driven by TRANSFORM, never width or
// height, so the compositor can run them off the main thread.
//
// That last point is why .dv-fill scales rather than growing: animating width
// forces layout on every frame, and a dashboard with forty bars would drop
// frames on a range change.
const Motion = () => (
  <GlobalStyles
    styles={{
      "@keyframes dv-rise": { from: { opacity: 0, transform: "translateY(28px) scale(.985)" } },
      "@keyframes dv-item": { from: { opacity: 0, transform: "translateY(10px)" } },
      "@keyframes dv-char": { from: { transform: "translateY(115%)" } },
      "@keyframes dv-growx": { from: { transform: "scaleX(0)" } },
      "@keyframes dv-growy": { from: { transform: "scaleY(0)" } },
      "@keyframes dv-pop": { from: { opacity: 0, transform: "scale(.4)" } },
      "@keyframes dv-fade": { from: { opacity: 0 } },
      "@keyframes dv-pulse": { "50%": { opacity: 0.25 } },
      "@keyframes dv-wipe": { from: { transform: "scaleX(1)" }, to: { transform: "scaleX(0)" } },
      "@keyframes dv-shimmer": { from: { backgroundPosition: "-220% 0" }, to: { backgroundPosition: "220% 0" } },

      ".dv-card": {
        animation: "dv-rise .8s cubic-bezier(.22,1,.36,1) backwards",
        transition: "transform .45s cubic-bezier(.22,1,.36,1), box-shadow .45s",
      },
      ".dv-card:hover": { transform: "translateY(-3px)" },
      ".dv-item": { animation: "dv-item .5s cubic-bezier(.22,1,.36,1) backwards" },
      ".dv-char": { display: "inline-block", animation: "dv-char .8s cubic-bezier(.16,1,.3,1) backwards" },
      ".dv-fade": { animation: "dv-fade .6s ease backwards" },
      ".dv-pop": { animation: "dv-pop .45s cubic-bezier(.34,1.56,.64,1) backwards" },
      ".dv-live": { animation: "dv-pulse 1.6s cubic-bezier(.22,1,.36,1) infinite" },

      // Transform-driven fills. The element is laid out at its FINAL size and
      // scaled down to start, so nothing reflows while it animates.
      ".dv-fill": { transformOrigin: "0 50%", animation: "dv-growx 1s cubic-bezier(.22,1,.36,1) backwards" },
      ".dv-bar": { transformOrigin: "50% 100%", animation: "dv-growy .8s cubic-bezier(.16,1,.3,1) backwards" },
      ".dv-wipe": { transformOrigin: "100% 50%", animation: "dv-wipe 1.1s cubic-bezier(.65,0,.35,1) both" },

      // "Unknown" is a residual bucket, not a result. Hatching it means an
      // admin can tell at a glance that the biggest bar in a list is missing
      // data rather than a finding.
      ".dv-hatch": { background: "repeating-linear-gradient(135deg, #DDD6EA 0 4px, #F4F1F8 4px 8px)" },

      ".dv-skel": {
        backgroundImage: "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,.055) 45%, rgba(0,0,0,0) 90%)",
        backgroundSize: "220% 100%",
        animation: "dv-shimmer 1.35s ease-in-out infinite",
      },

      "@media (prefers-reduced-motion: reduce)": {
        ".dv-card, .dv-item, .dv-char, .dv-fade, .dv-pop, .dv-live, .dv-fill, .dv-bar, .dv-wipe, .dv-skel": {
          animation: "none !important",
          transition: "none !important",
          transform: "none !important",
        },
        ".dv-wipe": { display: "none" },
      },
    }}
  />
);

export default Motion;
